"use client";

import { useCallback, useMemo, useState } from "react";
import { FolderClosed, MessageSquarePlus } from "lucide-react";
import { useRouter } from "next/navigation";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ErrorMessage } from "@/components/ui/error-message";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { SidebarGroup, SidebarMenu, SidebarMenuButton, SidebarMenuItem, useSidebar } from "@/components/ui/sidebar";
import { useNavigationData } from "@/hooks/useNavigationData";
import { useConversationStore } from "@/stores/useConversationStore";
import { useCurrentUserSharesStore } from "@/stores/useCurrentUserSharesStore";
import { useProjectStore } from "@/stores/useProjectStore";
import { getErrorMessage } from "@/types/problem-detail";
import { cn } from "@/lib/utils";
function resolveDefaultProjectId(projectIdFromUrl: string | null, projects: {
  id: string;
}[], currentProject: {
  id: string;
} | null): string {
  if (projectIdFromUrl) return projectIdFromUrl;
  if (currentProject && projects.some(p => p.id === currentProject.id)) {
    return currentProject.id;
  }
  return projects[0]?.id ?? "";
}
function isProjectViewer(projectId: string, sharesInitialized: boolean, roleForEntity: (entityType: "project", entityPublicId: string) => "editor" | "viewer" | "member" | null): boolean {
  return sharesInitialized && roleForEntity("project", projectId) === "viewer";
}
export function NavNewConversation() {
  const router = useRouter();
  const {
    state
  } = useSidebar();
  const isCollapsed = state === "collapsed";
  const {
    projectId,
    currentProject
  } = useNavigationData();
  const storeCurrentProject = useProjectStore(s => s.currentProject);
  const projects = useProjectStore(s_0 => s_0.projects);
  const fetchProjects = useProjectStore(s_1 => s_1.fetchProjects);
  const createProject = useProjectStore(s_2 => s_2.createProject);
  const createConversation = useConversationStore(s_3 => s_3.createConversation);
  const sharesInitialized = useCurrentUserSharesStore(s_4 => s_4.isInitialized);
  const roleForEntity = useCurrentUserSharesStore(s_5 => s_5.roleForEntity);
  const [open, setOpen] = useState(false);
  const [title, setTitle] = useState("");
  const [selectedProjectId, setSelectedProjectId] = useState("");
  const [newProjectName, setNewProjectName] = useState("");
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const isProjectLocked = Boolean(projectId);
  const creatableProjects = useMemo(() => projects.filter(p => !isProjectViewer(p.id, sharesInitialized, roleForEntity)), [projects, sharesInitialized, roleForEntity]);
  const hasProjects = creatableProjects.length > 0;
  const needsCreateProject = !isProjectLocked && projects.length === 0;
  const needsProjectPicker = !isProjectLocked && projects.length > 0;
  const lockedProjectViewer = isProjectLocked && projectId && isProjectViewer(projectId, sharesInitialized, roleForEntity);
  const sortedCreatableProjects = useMemo(() => [...creatableProjects].sort((a, b) => a.title.localeCompare(b.title)), [creatableProjects]);
  const lockedProject = useMemo(() => {
    if (!projectId) return null;
    return projects.find(p_0 => p_0.id === projectId) ?? currentProject;
  }, [projectId, projects, currentProject]);
  const resetDialogState = useCallback((projectList = creatableProjects, activeProject = storeCurrentProject) => {
    setTitle("");
    setNewProjectName("");
    setError(null);
    setSelectedProjectId(resolveDefaultProjectId(projectId, projectList, activeProject));
  }, [projectId, creatableProjects, storeCurrentProject]);
  const handleOpen = async () => {
    if (lockedProjectViewer) {
      toast.error("View only — you can't start conversations in this project.");
      return;
    }
    setError(null);
    await fetchProjects();
    const {
      projects: latestProjects,
      currentProject: latestCurrent
    } = useProjectStore.getState();
    const latestCreatable = latestProjects.filter(p_1 => !isProjectViewer(p_1.id, sharesInitialized, roleForEntity));
    if (!projectId && latestProjects.length > 0 && latestCreatable.length === 0) {
      toast.error("View only — you don't have permission to start conversations in these projects.");
      return;
    }
    resetDialogState(latestCreatable, latestCurrent);
    setOpen(true);
  };
  const handleOpenChange = (nextOpen: boolean) => {
    setOpen(nextOpen);
    if (!nextOpen) {
      resetDialogState();
    }
  };
  const canSubmit = title.trim().length > 0 && (isProjectLocked ? Boolean(projectId) && !lockedProjectViewer : needsCreateProject ? newProjectName.trim().length > 0 : selectedProjectId.length > 0 && !isProjectViewer(selectedProjectId, sharesInitialized, roleForEntity));
  const handleCreate = async () => {
    if (!canSubmit) return;
    setCreating(true);
    setError(null);
    try {
      let resolvedProjectId = isProjectLocked ? projectId : selectedProjectId;
      if (needsCreateProject) {
        const created = await createProject(newProjectName.trim());
        resolvedProjectId = created.id;
      }
      if (!resolvedProjectId) {
        throw new Error("Select a project before starting a conversation.");
      }
      if (isProjectViewer(resolvedProjectId, sharesInitialized, roleForEntity)) {
        throw new Error("View only — you can't start conversations in this project.");
      }
      const conversation = await createConversation(title.trim(), resolvedProjectId);
      setOpen(false);
      resetDialogState();
      router.push(`/dashboard/projects/${resolvedProjectId}/conversations/${conversation.id}`);
    } catch (e) {
      const message = getErrorMessage(e, "Failed to create conversation.");
      setError(message);
      toast.error(message);
    } finally {
      setCreating(false);
    }
  };
  const dialogDescription = isProjectLocked ? "Start a new conversation in the current project." : needsCreateProject ? "Create your first project, then start the conversation." : "Choose a project, then name your conversation.";
  const submitLabel = needsCreateProject ? creating ? "Creating…" : "Create project & start" : creating ? "Creating…" : "Create";
  if (lockedProjectViewer) {
    return null;
  }
  return <>
      <SidebarGroup className="py-0">
        <SidebarMenu>
          <SidebarMenuItem>
            <SidebarMenuButton tooltip="New conversation" onClick={() => void handleOpen()} className={cn("bg-primary text-primary-foreground hover:bg-primary/90 hover:text-primary-foreground", isCollapsed && "justify-center")}>
              <MessageSquarePlus className="size-4" />
              {!isCollapsed ? <span>New conversation</span> : null}
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarGroup>

      <Dialog open={open} onOpenChange={handleOpenChange}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>New conversation</DialogTitle>
            <DialogDescription>{dialogDescription}</DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            {isProjectLocked ? <div className="space-y-2">
                <Label>Project</Label>
                <div className="flex items-center gap-2 rounded-md border bg-muted/40 px-3 py-2 text-sm">
                  <FolderClosed className="size-4 shrink-0 text-muted-foreground" />
                  <span className="truncate font-medium">
                    {lockedProject?.title ?? "Loading project…"}
                  </span>
                </div>
              </div> : null}

            {needsProjectPicker ? <div className="space-y-2">
                <Label htmlFor="sidebar-new-conversation-project">Project</Label>
                {hasProjects ? <Select value={selectedProjectId} onValueChange={setSelectedProjectId}>
                    <SelectTrigger id="sidebar-new-conversation-project" className="w-full">
                      <SelectValue placeholder="Select a project" />
                    </SelectTrigger>
                    <SelectContent>
                      {sortedCreatableProjects.map(project => <SelectItem key={project.id} value={project.id}>
                          {project.title}
                        </SelectItem>)}
                    </SelectContent>
                  </Select> : <p className="text-sm text-muted-foreground">
                    You only have view-only access to projects in this workspace.
                  </p>}
              </div> : null}

            {needsCreateProject ? <div className="space-y-2">
                <Label htmlFor="sidebar-new-conversation-project-name">
                  Project name
                </Label>
                <Input id="sidebar-new-conversation-project-name" value={newProjectName} onChange={e_0 => setNewProjectName(e_0.target.value)} placeholder="e.g. Meal prep app" onKeyDown={e_1 => {
              if (e_1.key === "Enter" && canSubmit) {
                void handleCreate();
              }
            }} />
              </div> : null}

            <div className="space-y-2">
              <Label htmlFor="sidebar-new-conversation-title">Conversation title</Label>
              <Input id="sidebar-new-conversation-title" value={title} onChange={e_2 => setTitle(e_2.target.value)} placeholder="e.g. Meal prep idea" disabled={needsProjectPicker && !hasProjects} onKeyDown={e_3 => {
              if (e_3.key === "Enter" && canSubmit) {
                void handleCreate();
              }
            }} />
            </div>

            <ErrorMessage message={error} />
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} disabled={creating}>
              Cancel
            </Button>
            <Button onClick={() => void handleCreate()} disabled={creating || !canSubmit}>
              {submitLabel}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>;
}